var _excluded = ["children", "trigger", "defaultOpened", "placement", "fallbackPlacements", "opened", "dangerouslySetClassNames", "closeOnClickOutside", "onClickOutside", "zIndex", "initialFocusRef", "className"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useEffect, useRef, useState } from "react";
import composeRefs from "@seznam/compose-react-refs";
import styled from "styled-components";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import forwardRef from "../../private/forwardRef";
import usePopper from "../../hooks/usePopper";
import { flip, shift, limitShift } from "@floating-ui/react";
import Portal from "../../private/Portal";
var paddingHelper = _ref => {
  var {
    $placement,
    theme
  } = _ref;
  var medium = theme.spacing.medium;
  var padding = {
    top: medium + " " + medium + " 0 " + medium,
    right: medium + " " + medium + " " + medium + " 0",
    left: medium + " 0 " + medium + " " + medium,
    bottom: "0 " + medium + " " + medium + " " + medium,
    "top-start": medium + " " + medium + " 0 0",
    "bottom-start": "0 " + medium + " " + medium + " 0",
    "right-start": "0 " + medium + " " + medium + " 0",
    "left-start": "0 0 " + medium + " " + medium,
    "left-end": medium + " 0 0 " + medium,
    "right-end": medium + " " + medium + " 0 0",
    "bottom-end": " 0 0 " + medium + " " + medium,
    "top-end": " " + medium + " 0 0 " + medium
  };
  return padding[$placement];
};
var Trigger = styled.span.withConfig({
  displayName: "ElementPopover__Trigger",
  componentId: "sc-1fr9a6p-0"
})(["width:auto;display:inline-block;"]);
var Overlay = styled.span.withConfig({
  displayName: "ElementPopover__Overlay",
  componentId: "sc-1fr9a6p-1"
})(["z-index:", ";padding:", ";"], _ref2 => {
  var {
    $zIndex
  } = _ref2;
  return $zIndex;
}, paddingHelper);
var defaultFallbackPlacementsFor = {
  "top-start": ["bottom-start", "right-start", "left-start"],
  top: ["bottom", "right", "left"],
  "top-end": ["bottom-end", "right-end", "left-end"],
  "left-start": ["right-start", "bottom-start", "top-start"],
  left: ["right", "bottom", "top"],
  "left-end": ["right-end", "bottom-end", "top-end"],
  "right-start": ["left-start", "bottom-start", "top-start"],
  right: ["left", "bottom", "top"],
  "right-end": ["left-end", "bottom-end", "top-end"],
  "bottom-start": ["top-start", "right-start", "left-start"],
  bottom: ["top", "right", "left"],
  "bottom-end": ["top-end", "right-end", "left-end"]
};

/**
 * Anchors overlayed UI elements to another element on the page.
 *
 * @see [ElementPopover documentation](https://construct-kit.reainternal.net/components/web/core/element-popover)
 */
var ElementPopover = forwardRef((_ref3, externalRef) => {
  var {
      children,
      trigger,
      defaultOpened = false,
      placement = "bottom",
      fallbackPlacements,
      opened,
      dangerouslySetClassNames,
      closeOnClickOutside = true,
      onClickOutside,
      zIndex = 90,
      initialFocusRef,
      className
    } = _ref3,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded);
  var [isOpen, setOpen] = useState(defaultOpened);
  var {
    refs,
    popperRef,
    popperStyle,
    placement: calculatedPlacement,
    context
  } = usePopper({
    placement,
    mounted: isOpen || opened,
    middleware: [shift({
      limiter: limitShift()
    }), flip({
      fallbackPlacements: fallbackPlacements || defaultFallbackPlacementsFor[placement]
    })]
  });
  var overlayWrapperRef = useRef(null);
  var triggerRef = useRef(null);
  var toggle = () => {
    setOpen(!isOpen);
  };
  var close = () => {
    setOpen(false);
  };
  var open = () => {
    setOpen(true);
  };
  useOnClickOutside(overlayWrapperRef, triggerRef, event => {
    onClickOutside == null || onClickOutside(event);
    closeOnClickOutside && close();
  }, !!onClickOutside || closeOnClickOutside);
  useEffect(() => {
    if ((opened === undefined ? isOpen : opened) && context.isPositioned) {
      var _initialFocusRef$curr;
      initialFocusRef == null || (_initialFocusRef$curr = initialFocusRef.current) == null || _initialFocusRef$curr.focus({
        preventScroll: true
      });
    }
  }, [initialFocusRef, isOpen, context.isPositioned, opened]);
  var renderOverlay = (popperRef, style, placement) => /*#__PURE__*/React.createElement("span", {
    className: "CK__ElementPopover__OverlayWrapper " + (className || ""),
    ref: overlayWrapperRef
  }, /*#__PURE__*/React.createElement(Overlay, {
    className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.overlay,
    ref: popperRef,
    style: style,
    "data-placement": placement,
    $placement: calculatedPlacement,
    $zIndex: zIndex
  }, children(close)));
  return /*#__PURE__*/React.createElement(Trigger, _extends({
    className: "CK__ElementPopover__Root " + (className || ""),
    ref: composeRefs(triggerRef, refs.reference, externalRef)
  }, props), trigger({
    toggle,
    close,
    open,
    isOpen: opened === undefined ? isOpen : opened
  }), /*#__PURE__*/React.createElement(Portal, {
    id: "construct-kit-popover-portal",
    trigger: triggerRef.current,
    portalElement: overlayWrapperRef.current
  }, (opened === undefined ? isOpen : opened) && /*#__PURE__*/React.createElement(React.Fragment, null, renderOverlay(popperRef, popperStyle, placement), /*#__PURE__*/React.createElement("div", {
    "aria-hidden": "true"
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    ,
    tabIndex: 0,
    style: _extends({}, popperStyle, {
      opacity: 0
    })
  }))));
});
ElementPopover.displayName = "ElementPopover";
export default ElementPopover;